'use client';

import { useCallback, useEffect, useState } from 'react';

import type { SwipeBackOption } from '@weberus/ui/utils/swipe-back-options';
import { yaNative } from '@weberus/ui/utils/ya-native';
import { isServer } from '@weberus/utils';
import { useRouter } from 'next/navigation';

import { useHash } from './use-hash';

import type { ModalHashAppRouterId } from './use-escape-key-app-router';

type ModalHashAppRouter = (param: {
  /**
   * useEscapeRouterBack 실행 조건을 더 엄격하게 관리하기 위해 리터럴타입 규칙추가
   * bottom-sheet, modal, popup postfix를 가진 id만 허용
   * */
  id: ModalHashAppRouterId;
  swipeBackOption?: SwipeBackOption;
}) => [boolean, (isOpened: boolean) => void];

const idMap: Record<string, boolean> = {}; // 등록된 모달이 열려있는지 체크하는 맵

/**
 * getHashInfo
 * - 주어진 모달 ID를 기준으로 현재 URL 해시 상태를 분석합니다.
 *
 * @param id {string} 모달에 해당하는 고유 ID
 * @returns {Object}
 *          hash: 현재 해시 값 (문자열)
 *          hashStack: 해시 값을 '|'로 분리한 배열
 *          shouldOpen: 해당 ID의 모달이 열려 있어야 하는지 여부 (boolean)
 *
 * 설명:
 * - `idMap`에 모달 ID가 등록되어 있고, 현재 해시 스택에 해당 ID가 포함되어 있는 경우 `shouldOpen`은 true를 반환합니다.
 */
const getHashInfo = (id: string): { hash: string; hashStack: string[]; shouldOpen: boolean } => {
  const hash = window.location.hash;
  const hashStack = hash.slice(1).split('|');
  // [DEFECT-15304] 새로고침 시 `idMap`에 모달 ID가 없는 경우 모달을 표시하지 않습니다.
  const shouldOpen = !!idMap[id] && hashStack.filter((dialogId) => dialogId === id).length > 0;

  return {
    hash,
    hashStack,
    shouldOpen,
  };
};

/**
 * - URL 해시를 기반으로 모달 상태를 관리합니다.
 * - 해시 변경을 감지해 모달을 열거나 닫으며, 앱의 뒤로가기 동작과 연계됩니다.
 * - iOS에서 스와이프 백 동작과 연계됩니다.
 *
 * @param param {Object}
 *        id {ModalHashAppRouterId} 모달 고유 ID ('hash-' 접두사 포함)
 *        swipeBackOption {SwipeBackOption} iOS 스와이프 백 동작 설정 옵션 (선택적)
 * @returns {[boolean, Function]}
 *          isOpened: 모달이 열려 있는지 여부를 나타내는 상태 값
 *          setState: 모달 열림/닫힘 상태를 변경하는 함수
 *
 * 사용 예시:
 * const [isOpened, setState] = useHashModalAppRouter({
 *   id: 'example-modal',
 *   swipeBackOption: { onOpen: () => {}, onClose: () => {} },
 * });
 */
export const useHashModalAppRouter: ModalHashAppRouter = function ({ id, swipeBackOption }) {
  const hash = useHash();

  const router = useRouter();

  // 현재 모달의 열림 상태를 관리
  const [isOpened, setIsOpened] = useState<boolean>(() => {
    if (isServer()) {
      return false;
    }
    return getHashInfo(id).shouldOpen;
  });

  // 해시 변경 시 모달 열림 상태를 업데이트
  useEffect(() => {
    setIsOpened(getHashInfo(id).shouldOpen);
  }, [id, hash]);

  // 모달 열림 상태를 변경하는 함수
  const setState = useCallback(
    (_isOpened: boolean) => {
      const { hashStack, shouldOpen: isExist } = getHashInfo(id);
      idMap[id] = _isOpened; // [DEFECT-15304] 새로고침이 아닌 직접 진입한 모달만 노출하도록 값 지정

      /**
       * iOS의 스와이프 백 동작 처리
       *
       * 1. 설정하는 경우
       * 현재 hashbang 요소에 id가 하나도 없다면 그 상황이 가장 하단의 레이아웃 요소라고 판단할 수 있다.
       * (하나도 없다가 이제 추가될 것이기 때문)
       * 2. 설정 해제하는 경우
       * 현재 hashbang 요소에 아이디가 포함되며, hashbang 요소의 총 개수가 1개인 경우
       * (현재 열려있으며 이제 닫힐 것이기 때문)
       */
      const hasIdHashStackLength = hashStack.filter((t) => t).length;
      if (yaNative.isEnabled && swipeBackOption) {
        if (_isOpened && !hasIdHashStackLength) {
          swipeBackOption.onOpen();
        } else if (!_isOpened && isExist && hasIdHashStackLength <= 1) {
          swipeBackOption.onClose();
        }
      }

      if (_isOpened) {
        // 열때는 url 에 중복이 있는지 여부를 보고 해시에 아이디가 존재하지 않으면 추가한다
        if (!isExist) {
          const newHash = [...hashStack, id].filter((_id) => !!_id).join('|');
          const currentQuery = new URLSearchParams(window.location.search).toString();
          window.history.pushState(null, '', `${currentQuery ? `?${currentQuery}` : ''}#${newHash}`);

          // 새로고침 이후 모달 열리지 않는 문제 해결을 위해 해시값 변경 이후 상태를 직접 업데이트
          setIsOpened(getHashInfo(id).shouldOpen);
        }
      } else if (isExist) {
        // 닫을 때는 url 에 아이디가 존재하는지 여부를 보고 history back을 한다.
        // url 에 아이디가 존재하지 않으면 history back 을 통해 온것이거나 이미 닫힌 모달이다.
        router.back();
      }
    },
    [hash, swipeBackOption],
  );

  return [isOpened, setState];
};
